import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";

function diff(targetIso) {
  if (!targetIso) return null;
  const t = new Date(targetIso).getTime();
  if (isNaN(t)) return null;
  const ms = t - Date.now();
  if (ms <= 0) return { d: 0, h: 0, m: 0, s: 0, expired: true };
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return { d, h, m, s, expired: false };
}

function HeadlineText({ left, drop }) {
  if (left?.expired) {
    return (<>Drop is <span className="italic text-[var(--hk-orange)]">live</span></>);
  }
  if (drop?.drop_at) {
    return (<>Drops in <span className="italic text-[var(--hk-purple-text)]">{left ? `${left.d}d` : "—"}</span></>);
  }
  return (<>Be first <span className="italic text-[var(--hk-purple-text)]">to know</span></>);
}

export default function DropCountdown() {
  const [drop, setDrop] = useState(null);
  const [now, setNow] = useState(0);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/drop/next").then((r) => setDrop(r.data));
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Recomputed each render; the 1s `now` tick forces the re-render that updates the countdown.
  const left = diff(drop?.drop_at);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const payload = { email: email.trim() };
      if (phone.trim()) payload.phone = phone.trim();
      const { data } = await api.post("/newsletter/subscribe", payload);
      setDone(true);
      toast.success(data.already_subscribed ? "You're already on the list" : "You're on the list");
    } catch (err) {
      toast.error("Try again");
    }
    setBusy(false);
  };

  return (
    <section className="relative px-6 md:px-12 lg:px-24 py-20 md:py-28 border-t border-white/10 bg-gradient-to-b from-black via-[#0a0612] to-black">
      <div className="absolute inset-0 pointer-events-none opacity-30"
           style={{ backgroundImage: "radial-gradient(circle at 30% 50%, rgba(147,51,234,0.4), transparent 60%)" }} />
      <div className="relative grid md:grid-cols-2 gap-12 md:gap-16 items-center">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <Sparkles size={14} className="text-[var(--hk-orange)]" />
            <span className="font-mono-alt text-xs tracking-[0.4em] text-[var(--hk-orange)]">
              / {drop?.title || "NEXT DROP"}
            </span>
          </div>
          <h2 className="font-display text-5xl md:text-7xl font-bold uppercase tracking-tighter leading-[0.9]">
            <HeadlineText left={left} drop={drop} />
          </h2>
          {drop?.subtitle && (
            <p className="mt-5 text-gray-300 text-base md:text-lg max-w-md">{drop.subtitle}</p>
          )}
          {drop?.drop_at && left && !left.expired && (
            <div className="mt-8 grid grid-cols-4 gap-3 max-w-md" data-testid="drop-countdown">
              {[
                { v: left.d, l: "Days" },
                { v: left.h, l: "Hours" },
                { v: left.m, l: "Min" },
                { v: left.s, l: "Sec" },
              ].map((u) => (
                <div key={u.l} className="border border-white/10 bg-black/60 backdrop-blur p-3 md:p-4 text-center">
                  <p className="font-display text-3xl md:text-4xl text-white tabular-nums">
                    {String(u.v).padStart(2, "0")}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mt-1">{u.l}</p>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="border border-[var(--hk-purple)]/40 bg-black/60 backdrop-blur-xl p-6 md:p-8"
        >
          <p className="font-mono-alt text-xs tracking-[0.4em] text-[var(--hk-purple-text)] mb-3">
            / DROP ALERTS
          </p>
          <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight mb-5">
            Get the link before everyone else
          </h3>
          <p className="text-sm text-gray-400 mb-6">
            Drop dates, lookbooks, and access codes — straight to your inbox. No spam.
          </p>
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="done"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-3 border border-[var(--hk-orange)]/40 bg-[var(--hk-orange)]/10 p-4"
              >
                <Mail className="text-[var(--hk-orange)]" size={18} />
                <p className="text-sm">You&apos;re on the list. Look out for the drop.</p>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={subscribe}
                className="space-y-3"
                data-testid="newsletter-form"
              >
                <input
                  data-testid="newsletter-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="w-full bg-transparent border-b border-white/20 focus:border-[var(--hk-purple)] outline-none py-3 px-2 text-white placeholder-gray-600"
                />
                <input
                  data-testid="newsletter-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(optional) phone for SMS alerts"
                  className="w-full bg-transparent border-b border-white/20 focus:border-[var(--hk-purple)] outline-none py-3 px-2 text-white placeholder-gray-600"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="newsletter-submit"
                  className="w-full bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-wider px-6 py-3 rounded-sm hover:bg-[var(--hk-orange-hover)] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {busy ? "..." : "Notify me"}
                </button>
                <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500">
                  Email required · phone optional · unsubscribe anytime
                </p>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
